import * as React from 'react';
import { EntityStatusValue } from '@web-config-app/core';
import { useGetEntityTypeName } from '@web-config-app/core-react';
import type {
  DeepPublishTreeEntry,
  DiscoveryFailure,
} from '@web-config-app/core-react';
import {
  DeepPublishConfirmationModal,
  type DeepPublishTreeNode,
} from '@web-config-app/core-react-ui';

interface DeepPublishModalContainerProps {
  isOpen: boolean;
  tree: DeepPublishTreeEntry[];
  failures: DiscoveryFailure[];
  isDiscovering: boolean;
  discoveryErrorMessage?: string;
  rootEntityId?: string;
  publishDangerously?: boolean;
  environmentName?: string;
  onConfirm: () => void;
  onClose: () => void;
}

const getDescendantCount = (node: DeepPublishTreeNode): number =>
  node.childNodes.reduce(
    (sum, child) => sum + 1 + getDescendantCount(child),
    0,
  );

/**
 * Recursively build child nodes from an entry's dependencies.
 * Dependencies = entities this entry references = shown as children in the tree.
 * Discovery failures for this parent are appended as error child nodes.
 */
const buildChildNodes = (
  parentEntry: DeepPublishTreeEntry,
  entryMap: Map<string, DeepPublishTreeEntry>,
  renderedOnce: Set<string>,
  failuresByParent: Map<string, DiscoveryFailure[]>,
): DeepPublishTreeNode[] => {
  const dependencyNodes: DeepPublishTreeNode[] = parentEntry.dependencies
    .filter((dep) => entryMap.has(dep.id))
    .map((dep) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- checked by filter above
      const childEntry = entryMap.get(dep.id)!;
      const isDuplicate = renderedOnce.has(dep.id);
      if (!isDuplicate) {
        renderedOnce.add(dep.id);
      }

      return {
        id: childEntry.entity.id,
        entityName: childEntry.entityName,
        domainEntityType: childEntry.domainEntityType,
        status: childEntry.status,
        link: childEntry.link,
        isDuplicate,
        childNodes: isDuplicate
          ? []
          : buildChildNodes(
              childEntry,
              entryMap,
              renderedOnce,
              failuresByParent,
            ),
      };
    });

  // Append nodes for entities that failed to load, shown under their parent
  const parentFailures = failuresByParent.get(parentEntry.entity.id);
  const failureNodes: DeepPublishTreeNode[] = (parentFailures ?? []).map(
    (failure, index) => ({
      id: `failure-${
        failure.domainEntityId ?? failure.configEntityId ?? index
      }`,
      entityName:
        failure.domainEntityId ?? failure.configEntityId ?? 'Unknown entity',
      domainEntityType: failure.domainEntityType ?? '',
      status: '',
      error: failure.message,
      childNodes: [],
    }),
  );

  const children = [...dependencyNodes, ...failureNodes];
  children.sort((a, b) => getDescendantCount(a) - getDescendantCount(b));

  return children;
};

/**
 * Builds a hierarchical tree from a flat list of discovered tree entries.
 *
 * The root entity is included in the entries (added during discovery with
 * dependencies pointing to all child entities). This function finds the root
 * entry and recursively builds child nodes from the dependency graph.
 *
 * Discovery failures are added as error child nodes beneath the parent entity
 * that referenced them (identified by `referencedFrom`). Failures without a
 * `referencedFrom` are attached to the root.
 */
export const buildTreeNode = (
  rootEntityId: string,
  treeEntries: DeepPublishTreeEntry[],
  failures: DiscoveryFailure[] = [],
): DeepPublishTreeNode | undefined => {
  const entryMap = new Map<string, DeepPublishTreeEntry>(
    treeEntries.map((entry) => [entry.entity.id, entry]),
  );

  const rootEntry = entryMap.get(rootEntityId);
  if (!rootEntry) return undefined;

  // Group failures by their parent entity (referencedFrom).
  // Failures without a referencedFrom get attached to the root.
  const failuresByParent = failures.reduce((map, failure) => {
    const parentId = failure.referencedFrom ?? rootEntityId;
    const existing = map.get(parentId) ?? [];
    existing.push(failure);
    map.set(parentId, existing);
    return map;
  }, new Map<string, DiscoveryFailure[]>());

  const renderedOnce = new Set<string>();
  renderedOnce.add(rootEntityId);

  return {
    id: rootEntry.entity.id,
    entityName: rootEntry.entityName,
    domainEntityType: rootEntry.domainEntityType,
    status: rootEntry.status,
    link: rootEntry.link,
    isDuplicate: false,
    childNodes: buildChildNodes(
      rootEntry,
      entryMap,
      renderedOnce,
      failuresByParent,
    ),
  };
};

export const DeepPublishModalContainer = ({
  isOpen,
  tree,
  failures,
  isDiscovering,
  discoveryErrorMessage,
  rootEntityId,
  publishDangerously,
  environmentName,
  onConfirm,
  onClose,
}: DeepPublishModalContainerProps) => {
  const getEntityTypeName = useGetEntityTypeName();

  const rootNode = React.useMemo(() => {
    if (!rootEntityId || tree.length === 0) return undefined;
    return buildTreeNode(rootEntityId, tree, failures);
  }, [rootEntityId, tree, failures]);

  const publishableCount = React.useMemo(
    () =>
      tree.filter(
        (entry) =>
          entry.status !== EntityStatusValue.Published &&
          entry.status !== EntityStatusValue.Archived,
      ).length,
    [tree],
  );

  const translateNodeTypes = React.useCallback(
    (node: DeepPublishTreeNode): DeepPublishTreeNode => ({
      ...node,
      domainEntityType: getEntityTypeName(node.domainEntityType, 1),
      childNodes: node.childNodes.map(translateNodeTypes),
    }),
    [getEntityTypeName],
  );

  const translatedRootNode = React.useMemo(
    () => (rootNode ? translateNodeTypes(rootNode) : undefined),
    [rootNode, translateNodeTypes],
  );

  return (
    <DeepPublishConfirmationModal
      isOpen={isOpen}
      isLoading={isDiscovering}
      rootNode={translatedRootNode}
      errorMessage={discoveryErrorMessage}
      hasFailures={failures.length > 0}
      publishableCount={publishableCount}
      publishDangerously={publishDangerously}
      environmentName={environmentName}
      onConfirm={onConfirm}
      onClose={onClose}
    />
  );
};
